import { useState } from 'react';
import { TickerPicker, ErrorNote, API_URL, fetchJson, fmt } from './shared';

const ROWS = [
  { key: 'one_day', label: '1-day typical range' },
  { key: 'five_day', label: '5-day typical range' },
  { key: 'twenty_day', label: '20-day typical range' },
];

export const VolatilityRange = ({ ready }) => {
  const [market, setMarket] = useState('');
  const [ticker, setTicker] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams({ ticker: ticker.trim(), market });
      setData(await fetchJson(`${API_URL}/api/tools/volatility-range?${q}`));
    } catch (e) { setError(e.message); setData(null); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="volatility-tool">
      <TickerPicker prefix="vol" market={market} setMarket={setMarket} ticker={ticker} setTicker={setTicker}
        onSubmit={run} loading={loading} disabled={!ready || !market || !ticker.trim()} cta="Calculate" />
      <ErrorNote error={error} testId="vol-error" />
      {data && (
        <div className="space-y-4 animate-fade-in" data-testid="vol-results">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Last price" value={fmt(data.current_price)} testId="vol-price" />
            <Stat label="Daily volatility" value={`${data.daily_volatility_pct}%`} testId="vol-daily" />
            <Stat label="Annualised" value={`${data.annualised_volatility_pct}% · ${data.volatility_level}`} testId="vol-annual" />
            <Stat label="ATR (14)" value={`${fmt(data.atr_14)} (${data.atr_pct}%)`} testId="vol-atr" />
          </div>
          <div className="border border-zinc-800 rounded-lg overflow-hidden">
            {ROWS.map((r) => {
              const b = data.ranges[r.key];
              return (
                <div key={r.key} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.4fr_1fr_1fr_0.6fr] gap-2 px-4 py-3 border-b border-zinc-800/60 last:border-0 text-sm" data-testid={`vol-row-${r.key}`}>
                  <span className="text-zinc-400 text-xs sm:text-sm">{r.label}</span>
                  <span className="font-mono text-right sm:text-left">{fmt(b.low)}</span>
                  <span className="font-mono hidden sm:block">{fmt(b.high)}</span>
                  <span className="font-mono text-zinc-500 hidden sm:block text-right">±{b.pct}%</span>
                  <span className="font-mono text-zinc-500 text-xs sm:hidden col-span-2 text-right">to {fmt(b.high)} (±{b.pct}%)</span>
                </div>
              );
            })}
            <div className="grid grid-cols-[1fr_auto] px-4 py-3 bg-zinc-900/40 text-sm" data-testid="vol-observed">
              <span className="text-zinc-400 text-xs sm:text-sm">Observed 30-day low / high</span>
              <span className="font-mono">{fmt(data.observed_30d.low)} / {fmt(data.observed_30d.high)}</span>
            </div>
          </div>
          <p className="text-[11px] text-zinc-600">{data.note} {data.data_freshness}.</p>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-md p-3">
    <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
    <p className="font-mono text-sm" data-testid={testId}>{value}</p>
  </div>
);
